// Polyfill declarations for older browsers
export {};

declare global {
  interface Array<T> {
    findIndex(predicate: (value: T, index: number, obj: T[]) => boolean, thisArg?: any): number;
    find(predicate: (value: T, index: number, obj: T[]) => boolean, thisArg?: any): T | undefined;
    swap(x: number, y: number): T[];
  }

  interface String {
    includes(searchString: string, position?: number): boolean;
    startsWith(searchString: string, position?: number): boolean;
    endsWith(searchString: string, position?: number): boolean;
  }
}

if (!Array.prototype.findIndex) {
  console.log('[APP-START] polyfill Array.prototype.findIndex');

  Object.defineProperty(Array.prototype, 'findIndex', {
    value<T>(
      this: T[],
      predicate: (value: T, index: number, obj: T[]) => boolean,
      thisArg?: any
    ): number {
      // 1. Let O be ? ToObject(this value).
      if (this == null) {
        throw new TypeError('"this" is null or not defined');
      }

      const o = Object(this);

      // 2. Let len be ? ToLength(? Get(O, "length")).
      const len = o.length >>> 0; //eslint-disable-line

      // 3. If IsCallable(predicate) is false, throw a TypeError exception.
      if (typeof predicate !== 'function') {
        throw new TypeError('predicate must be a function');
      }

      // 4. If thisArg was supplied, let T be thisArg; else let T be undefined.
      const thisArgument = thisArg; //eslint-disable-line

      // 5. Let k be 0.
      let k = 0;

      // 6. Repeat, while k < len
      while (k < len) {
        // a. Let Pk be ! ToString(k).
        // b. Let kValue be ? Get(O, Pk).
        // c. Let testResult be ToBoolean(? Call(predicate, T, « kValue, k, O »)).
        // d. If testResult is true, return k.
        const kValue = o[k];
        if (predicate.call(thisArgument, kValue, k, o)) {
          return k;
        }
        // e. Increase k by 1.
        k++; //eslint-disable-line
      }

      // 7. Return -1.
      return -1;
    },
    configurable: true,
    writable: true
  });
}

if (!String.prototype.includes) {
  console.log('[APP-START] polyfill String.prototype.includes');

  String.prototype.includes = function (str: string): boolean {
    let returnValue = false;

    if (this.indexOf(str) !== -1) {
      returnValue = true;
    }

    return returnValue;
  };
}

if (!String.prototype.startsWith) {
  console.log('[APP-START] polyfill String.prototype.startsWith');

  String.prototype.startsWith = function (searchString: string, position?: number): boolean {
    position = position || 0; //eslint-disable-line

    return this.indexOf(searchString, position) === position;
  };
}

if (!String.prototype.endsWith) {
  console.log('[APP-START] polyfill String.prototype.endsWith');

  String.prototype.endsWith = function (searchString: string, position?: number): boolean {
    const subjectString = this.toString();
    if (
      typeof position !== 'number' ||
      !Number.isFinite(position) ||
      Math.floor(position) !== position ||
      position > subjectString.length
    ) {
      position = subjectString.length; //eslint-disable-line
    }
    position -= searchString.length; //eslint-disable-line
    const lastIndex = subjectString.indexOf(searchString, position);

    return lastIndex !== -1 && lastIndex === position;
  };
}

if (!(Array.prototype as any).swap) {
  console.log('[APP-START] polyfill Array.prototype.swap');

  (Array.prototype as any).swap = function <T>(this: T[], x: number, y: number): T[] {
    const b = this[x];
    this[x] = this[y];
    this[y] = b;

    return this;
  };
}

if (!Array.prototype.find) {
  console.log('[APP-START] polyfill Array.prototype.find');

  Object.defineProperty(Array.prototype, 'find', {
    value<T>(
      this: T[],
      predicate: (value: T, index: number, obj: T[]) => boolean,
      thisArg?: any
    ): T | undefined {
      // 1. Let O be ? ToObject(this value).
      if (this == null) {
        throw new TypeError('"this" is null or not defined');
      }

      const o = Object(this);

      // 2. Let len be ? ToLength(? Get(O, "length")).
      const len = o.length >>> 0; //eslint-disable-line

      // 3. If IsCallable(predicate) is false, throw a TypeError exception.
      if (typeof predicate !== 'function') {
        throw new TypeError('predicate must be a function');
      }

      // 4. If thisArg was supplied, let T be thisArg; else let T be undefined.
      const thisArgument = thisArg; //eslint-disable-line

      // 5. Let k be 0.
      let k = 0;

      // 6. Repeat, while k < len
      while (k < len) {
        // a. Let Pk be ! ToString(k).
        // b. Let kValue be ? Get(O, Pk).
        // c. Let testResult be ToBoolean(? Call(predicate, T, « kValue, k, O »)).
        // d. If testResult is true, return kValue.
        const kValue = o[k];
        if (predicate.call(thisArgument, kValue, k, o)) {
          return kValue;
        }
        // e. Increase k by 1.
        k++; //eslint-disable-line
      }

      // 7. Return undefined.
      return undefined;
    },
    configurable: true,
    writable: true
  });
}

// Note: This is a duplicate endsWith implementation - keeping for compatibility
if (!String.prototype.endsWith) {
  console.log('[APP-START] polyfill String.prototype.endsWith (duplicate)');
  String.prototype.endsWith = function (searchString: string, position?: number): boolean {
    const subjectString = this.toString();
    if (
      typeof position !== 'number' ||
      !Number.isFinite(position) ||
      Math.floor(position) !== position ||
      position > subjectString.length
    ) {
      position = subjectString.length; //eslint-disable-line
    }
    position -= searchString.length; //eslint-disable-line
    const lastIndex = subjectString.indexOf(searchString, position);

    return lastIndex !== -1 && lastIndex === position;
  };
}
